import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'
import { InputField } from '../../../components/Forms'
import styles from '../CreateTripActivityPage/CreateTripActivityPage.module.scss'
import DefaultButton from '../../../components/Shared/Buttons/DefaultButton'
import SelectInput from '../../../components/Forms/SelectField'
import TextareaField from '../../../components/Forms/TextareaField'
import { AccommodationType, Currency } from '../../../constants/enums'
import classNames from 'classnames'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import GooglePlacesAutocomplete from '../../../components/Forms/GooglePlacesAutocomplete'
import tripService from '../../../supabaseServices/TripService'
import accommodationService from '../../../supabaseServices/AccommodationService'
import { toFixedISOString } from '../../../utils/format'
import { setSnackbar } from '../../../store/snackbar/actions'
import { useDispatch } from 'react-redux'

interface AccommodationFormValues {
  id?: number
  uuid?: string
  name: string
  physical_address: string
  access_instructions: string
  wifi_info: string
  type: string
  bedrooms: number | ''
  bathrooms: number | ''
  max_capacity: number | ''
  check_in: Date | null
  check_out: Date | null
  price: number | ''
  currency: string
  listing_link: string
  location?: any
  locations?: any
}

interface SelectOption {
  id: string
  value: string
  label: string
}

const AccommodationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  type: Yup.string().required('Type is required'),
  check_in: Yup.date().required('Check-in date is required'),
  check_out: Yup.date()
    .nullable()
    .min(Yup.ref('check_in'), 'Check-out cannot be earlier than check-in'),
  location: Yup.lazy((value) => {
    if (typeof value === 'string') {
      return Yup.string().trim().required('Location is required')
    } else if (typeof value === 'object' && value !== null) {
      return Yup.object().required('Location is required')
    }
    return Yup.mixed().required('Location is required')
  }),
})

interface CreateAccommodationFormProps {
  tripId: string
  initialValues?: AccommodationFormValues
}

const CreateAccommodationForm: React.FC<CreateAccommodationFormProps> = ({
  tripId,
  initialValues,
}) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const [types, setTypes] = useState<SelectOption[]>([])
  const [currencies, setCurrencies] = useState<SelectOption[]>([])
  const [tripStartDate, setTripStartDate] = useState<Date | undefined>(undefined)
  const [tripEndDate, setTripEndDate] = useState<Date | undefined>(undefined)

  const handleOnSubmit = async (
    values: AccommodationFormValues,
    { setSubmitting, resetForm }: FormikHelpers<AccommodationFormValues>,
  ) => {
    try {
      const checkInStr = toFixedISOString(values.check_in)
      const checkOutStr = toFixedISOString(values.check_out)

      const payload: any = {
        ...values,
        trip_uuid: tripId,
        check_in: checkInStr ? new Date(checkInStr) : null,
        check_out: checkOutStr ? new Date(checkOutStr) : null,
      }

      if (initialValues?.id) {
        await accommodationService.updateAccommodation(initialValues.uuid!, payload)
        dispatch(
          setSnackbar({
            open: true,
            severity: 'success',
            message: 'Accommodation updated successfully!',
          }),
        )
      } else {
        await accommodationService.createAccommodation(payload)
        dispatch(
          setSnackbar({
            open: true,
            severity: 'success',
            message: 'Accommodation created successfully!',
          }),
        )
      }

      resetForm()
      history.push(`/trips/${tripId}`)
    } catch (error: any) {
      console.error('Error submitting the form:', error)
      const errorMessage = error?.message || error?.error?.message || 'Failed to save accommodation'
      dispatch(
        setSnackbar({
          open: true,
          severity: 'error',
          message: errorMessage,
        }),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const getCurrencyOptions = (setCurrencies: Dispatch<SetStateAction<SelectOption[]>>): void => {
    const currencies = Object.values(Currency).map((currency) => ({
      id: currency,
      value: currency,
      label: currency,
    }))
    setCurrencies(currencies)
  }

  const getTypeOptions = (setTypes: Dispatch<SetStateAction<SelectOption[]>>): void => {
    const types = Object.values(AccommodationType).map((type) => ({
      id: type,
      value: type,
      label: type.charAt(0) + type.slice(1).toLowerCase(),
    }))
    setTypes(types)
  }

  useEffect(() => {
    const fetchTripData = async () => {
      try {
        const trip = await tripService.getTripByUUID(tripId)
        if (trip) {
          setTripStartDate(new Date(trip.start_date))
          setTripEndDate(new Date(trip.end_date))
        }
      } catch (error) {
        console.error('Error fetching trip data:', error)
      }
    }

    fetchTripData()
    getTypeOptions(setTypes)
    getCurrencyOptions(setCurrencies)
  }, [tripId])

  const inputClasses = classNames(styles.input)

  return (
    <div className={styles.formWrapper}>
      <Formik
        initialValues={{
          name: initialValues?.name || '',
          type: initialValues?.type || '',
          physical_address: initialValues?.physical_address || '',
          access_instructions: initialValues?.access_instructions || '',
          wifi_info: initialValues?.wifi_info || '',
          bedrooms: initialValues?.bedrooms || '',
          bathrooms: initialValues?.bathrooms || '',
          max_capacity: initialValues?.max_capacity || '',
          check_in: initialValues?.check_in ? new Date(initialValues.check_in) : (tripStartDate ?? null),
          check_out: initialValues?.check_out ? new Date(initialValues.check_out) : (tripEndDate ?? null),
          price: initialValues?.price || '',
          currency: initialValues?.currency || 'USD',
          listing_link: initialValues?.listing_link || '',
          location: initialValues?.locations || null,
        }}
        validationSchema={AccommodationSchema}
        onSubmit={handleOnSubmit}
        enableReinitialize
      >
        {({ isValid, dirty, values, setFieldValue, errors, isSubmitting }) => (
          <Form>
            <div className={styles.row}>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Accommodation Name *</p>
                <InputField type="text" name="name" placeholder="e.g. Beach House Villa" />
              </div>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Type *</p>
                <Field name="type" className={inputClasses}>
                  {({ field, form }: any) => (
                    <SelectInput {...field} form={form} options={types} className={inputClasses} />
                  )}
                </Field>
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Location *</p>
                <Field name="location" className={inputClasses}>
                  {({ field, form }: any) => (
                    <GooglePlacesAutocomplete {...field} form={form} className={inputClasses} />
                  )}
                </Field>
              </div>
            </div>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div className={styles.row}>
                <div className={styles.rowItem}>
                  <p className={styles.rowItemLabel}>Check-in *</p>
                  <DateTimePicker
                    name="check_in"
                    onChange={(date) => setFieldValue('check_in', date)}
                    value={values.check_in}
                    minDateTime={tripStartDate}
                    maxDateTime={tripEndDate}
                  />
                </div>
                <div className={styles.rowItem}>
                  <p className={styles.rowItemLabel}>Check-out</p>
                  <DateTimePicker
                    name="check_out"
                    onChange={(date) => setFieldValue('check_out', date)}
                    value={values.check_out}
                    minDateTime={tripStartDate}
                    maxDateTime={tripEndDate}
                  />
                  {errors.check_out && <div className={styles.errorMsg}>{errors.check_out}</div>}
                </div>
              </div>
            </LocalizationProvider>

            <div className={styles.row}>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Bedrooms</p>
                <InputField type="number" name="bedrooms" placeholder="Number of bedrooms" />
              </div>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Bathrooms</p>
                <InputField type="number" name="bathrooms" placeholder="Number of bathrooms" />
              </div>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Max Capacity</p>
                <InputField type="number" name="max_capacity" placeholder="Max guests" />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Price (cents)</p>
                <InputField type="number" name="price" placeholder="Price in cents" />
              </div>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Currency</p>
                <Field name="currency" className={inputClasses}>
                  {({ field, form }: any) => (
                    <SelectInput {...field} form={form} options={currencies} className={inputClasses} />
                  )}
                </Field>
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.rowItemFull}>
                <p className={styles.rowItemLabel}>Listing Link</p>
                <InputField type="text" name="listing_link" placeholder="https://..." />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.rowItemFull}>
                <p className={styles.rowItemLabel}>Access Instructions</p>
                <Field name="access_instructions" className={inputClasses}>
                  {({ field, form }: any) => (
                    <TextareaField {...field} form={form} className={inputClasses} placeholder="How to access the property, key codes, etc." />
                  )}
                </Field>
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.rowItemFull}>
                <p className={styles.rowItemLabel}>WiFi Info</p>
                <InputField type="text" name="wifi_info" placeholder="Network name and password" />
              </div>
            </div>

            <div className={styles.btnsWrapper}>
              <DefaultButton
                buttonStyles={{ marginRight: '16px' }}
                onClick={() => history.goBack()}
                outlined={true}
                title={'Cancel'}
              />
              <DefaultButton
                disabled={!isValid || !dirty || isSubmitting}
                type={'submit'}
                title={initialValues ? 'Update' : 'Create'}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default CreateAccommodationForm
